"use client";

import { c as _c } from "react/compiler-runtime";
import { Locale } from "@/types/locale";
import { IconActivity, IconAlertCircle, IconInbox, IconListDetails, IconRefresh, IconStar, IconUsers } from "@tabler/icons-react";
import React, { useCallback, useMemo, useState } from "react";
import { useLocale } from "next-intl";
import Link from "next/link";
import { useSidePanel } from "@components/common/containers/side-panel";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@components/ui/empty";
import { PopoverDescription, PopoverHeader, PopoverTitle } from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import { Skeleton } from "@components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import { useAnyTranslations } from "@lib/i18n/utils";
import { cn } from "@lib/utils";
import { useGetProgramAssignmentAggregatesQuery } from "@features/events-program/program-api";
import { buildProgramAssignmentAggregatesQueryParams, ProposalAssignmentAggregateEntity } from "@features/events-program/types/proposal-assignment-aggregate";
import { getVotingPanelVoteVisibilityStorageKey, readVotingPanelVoteVisibilityStateFromSessionStorage } from "@features/events-program/ui/votes";
import { LazyActivityController } from "@features/proposals/controllers/lazy-activity-controller";
import { ActivityViewTabType } from "@features/proposals/ui/activity/activity-tabs-types";
import { AssignmentNotificationCategory, AssignmentNotificationItem, AssignmentNotificationPanelTab, buildAssignmentNotificationsModel, filterAssignmentNotificationItems } from "@features/notifications/assignment-notification-model";
const tabs: AssignmentNotificationPanelTab[] = ["all", "awaitingVote", "curatorship"];
const getTabCount = (tab: AssignmentNotificationPanelTab, counters: ReturnType<typeof buildAssignmentNotificationsModel>["counters"]) => tab === "all" ? counters.all : counters[tab];
const getQuickActionLabelKey = (tab: ActivityViewTabType) => tab === "vote" ? "actions.vote" : "actions.activity";
const statusPillClassNames: Record<AssignmentNotificationCategory, string> = {
  awaitingVote: "bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300",
  curatorship: "bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
  viewed: "bg-muted text-muted-foreground"
};
const statusDotClassNames: Record<AssignmentNotificationCategory, string> = {
  awaitingVote: "bg-yellow-500",
  curatorship: "bg-blue-500",
  viewed: "bg-gray-400 dark:bg-gray-500"
};
const AssignmentNotificationStatusPill = t0 => {
  const $ = _c(13);
  const {
    category
  } = t0;
  const t = useAnyTranslations("features.notifications.ui.assignmentNotifications");
  const t1 = statusPillClassNames[category];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex h-5 items-center gap-1 rounded-full px-2 text-xs font-medium whitespace-nowrap", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = statusDotClassNames[category];
  let t4;
  if ($[2] !== t3) {
    t4 = cn("size-1.5 rounded-full", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <span className={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `statuses.${category}`;
  let t7;
  if ($[6] !== t || $[7] !== t6) {
    t7 = t(t6);
    $[6] = t;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t2 || $[10] !== t5 || $[11] !== t7) {
    t8 = <span className={t2} data-testid="assignment-notification-status">{t5}{t7}</span>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
};
const AssignmentNotificationsLoading = t0 => {
  const $ = _c(6);
  const {
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="sr-only">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [0, 1, 2].map(_temp);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label || $[4] !== t1) {
    t3 = <div className="flex flex-col" aria-label={label}>{t1}{t2}</div>;
    $[3] = label;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const AssignmentNotificationsEmpty = t0 => {
  const $ = _c(8);
  const {
    description,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <EmptyMedia variant="icon"><IconInbox className="size-5" /></EmptyMedia>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <EmptyTitle className="text-sm">{title}</EmptyTitle>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== description) {
    t3 = <EmptyDescription>{description}</EmptyDescription>;
    $[3] = description;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Empty className="min-h-48 border-0 p-6"><EmptyHeader>{t1}{t2}{t3}</EmptyHeader></Empty>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const AssignmentNotificationsError = t0 => {
  const $ = _c(10);
  const {
    onRetry,
    retryLabel,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <EmptyMedia variant="icon" className="bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300"><IconAlertCircle className="size-5" /></EmptyMedia>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <EmptyHeader>{t1}<EmptyTitle className="text-sm">{title}</EmptyTitle></EmptyHeader>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IconRefresh data-icon="inline-start" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onRetry || $[5] !== retryLabel) {
    t4 = <EmptyContent><Button type="button" variant="outline" size="sm" onClick={onRetry}>{t3}{retryLabel}</Button></EmptyContent>;
    $[4] = onRetry;
    $[5] = retryLabel;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <Empty className="min-h-48 border-0 p-6">{t2}{t4}</Empty>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const AssignmentNotificationItemView = t0 => {
  const $ = _c(36);
  const {
    item,
    onQuickAction
  } = t0;
  const t = useAnyTranslations("features.notifications.ui.assignmentNotifications");
  const quickAction = item.quickAction;
  const QuickActionIcon = quickAction?.tab === "activity" ? IconActivity : IconStar;
  let t1;
  if ($[0] !== item.eventTitle) {
    t1 = item.eventTitle && <span className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">{item.eventTitle}</span>;
    $[0] = item.eventTitle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.categories) {
    t2 = item.categories.map(_temp2);
    $[2] = item.categories;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="ms-auto flex shrink-0 gap-1">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div className="flex min-w-0 items-center gap-2">{t1}{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== item.proposalTitle) {
    t5 = <h3 className="line-clamp-2 text-base leading-snug font-semibold">{item.proposalTitle}</h3>;
    $[9] = item.proposalTitle;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== item.speakerLine || $[12] !== item.topics) {
    t6 = (item.speakerLine || item.topics.length > 0) && <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">{item.speakerLine && <span className="inline-flex min-w-0 items-center gap-1"><IconUsers className="size-3.5 shrink-0" /><span className="truncate">{item.speakerLine}</span></span>}{item.topics.length > 0 && <span className="whitespace-nowrap">{item.topics.join(" \xB7 ")}</span>}</div>;
    $[11] = item.speakerLine;
    $[12] = item.topics;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== item.ratingLine || $[15] !== item.voteCountLine || $[16] !== t) {
    t7 = (item.ratingLine || item.voteCountLine) && <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">{item.ratingLine && <span className="inline-flex items-center gap-1 whitespace-nowrap"><IconStar className="size-3.5 shrink-0" />{t("meta.rating", {
          value: item.ratingLine
        })}</span>}{item.voteCountLine && <span className="whitespace-nowrap">{t("meta.votes", {
          value: item.voteCountLine
        })}</span>}</div>;
    $[14] = item.ratingLine;
    $[15] = item.voteCountLine;
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== QuickActionIcon || $[19] !== item || $[20] !== onQuickAction || $[21] !== quickAction || $[22] !== t) {
    t8 = quickAction && <Button type="button" variant="secondary" size="sm" onClick={() => onQuickAction(item)}><QuickActionIcon data-icon="inline-start" />{t(getQuickActionLabelKey(quickAction.tab))}</Button>;
    $[18] = QuickActionIcon;
    $[19] = item;
    $[20] = onQuickAction;
    $[21] = quickAction;
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== item.fullListHref || $[25] !== t) {
    t9 = item.fullListHref ? <Button variant="ghost" size="sm" className="text-muted-foreground" asChild={true}><Link href={item.fullListHref}><IconListDetails data-icon="inline-start" />{t("actions.fullList")}</Link></Button> : <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" disabled={true}><IconListDetails data-icon="inline-start" />{t("actions.fullList")}</Button>;
    $[24] = item.fullListHref;
    $[25] = t;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = <div className="flex flex-wrap items-center gap-1.5 pt-0.5">{t8}{t9}</div>;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7) {
    t11 = <article className="flex flex-col gap-2 border-b px-4 py-3 last:border-b-0" data-testid="assignment-notification-card">{t4}{t5}{t6}{t7}{t10}</article>;
    $[30] = t10;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};
const getAssignmentVoteVisibilityState = (aggregate: ProposalAssignmentAggregateEntity) => {
  const eventId = aggregate.event?.id ?? aggregate.proposal?.eventId ?? aggregate.proposal?.activity?.eventId;
  const currentTeamMemberId = aggregate.assignment?.assigneeId;
  if (eventId === undefined || currentTeamMemberId === undefined) {
    return undefined;
  }
  return readVotingPanelVoteVisibilityStateFromSessionStorage(getVotingPanelVoteVisibilityStorageKey(eventId, currentTeamMemberId));
};
export const useAssignmentNotificationsController = () => {
  const $ = _c(22);
  const locale = useLocale() as Locale;
  const t = useAnyTranslations("features.notifications.ui.assignmentNotifications");
  const {
    closePanel,
    isPanelOpen,
    openPanel,
    switchToPanel,
    updatePanel
  } = useSidePanel();
  const [selectedTab, setSelectedTab] = useState("all");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      query: buildProgramAssignmentAggregatesQueryParams()
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data,
    isError,
    isFetching,
    isLoading,
    refetch
  } = useGetProgramAssignmentAggregatesQuery(t0);
  let t1;
  if ($[1] !== data || $[2] !== locale) {
    t1 = buildAssignmentNotificationsModel(data, locale, {
      getVoteVisibilityState: getAssignmentVoteVisibilityState
    });
    $[1] = data;
    $[2] = locale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const model = t1;
  const isInitialLoading = isLoading || isFetching && model.items.length === 0;
  const badgeCount = isInitialLoading ? 0 : model.counters.requiresAction;
  let t2;
  if ($[4] !== closePanel || $[5] !== t) {
    t2 = item => {
      const quickAction = item.quickAction;
      const panelId = `activity-${quickAction?.activityId}`;
      return {
        title: item.proposalTitle,
        description: t("activityPanel.description"),
        size: "xl" as const,
        bookmarkLabel: t("activityPanel.bookmark"),
        children: quickAction ? <LazyActivityController key={`${quickAction.activityId}-${quickAction.tab}`} proposalId={quickAction.proposalId} activityId={quickAction.activityId} embedded={true} workflow={item.workflow} initActiveTab={quickAction.tab} closePanel={() => closePanel(panelId)} /> : undefined
      };
    };
    $[4] = closePanel;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const buildPanelConfig = t2;
  let t3;
  if ($[7] !== buildPanelConfig || $[8] !== isPanelOpen || $[9] !== openPanel || $[10] !== switchToPanel || $[11] !== updatePanel) {
    t3 = item_0 => {
      const quickAction_0 = item_0.quickAction;
      if (!quickAction_0) {
        return;
      }
      const panelId_0 = `activity-${quickAction_0.activityId}`;
      const config = buildPanelConfig(item_0);
      if (isPanelOpen(panelId_0)) {
        updatePanel(panelId_0, config);
        switchToPanel(panelId_0);
        return;
      }
      openPanel(panelId_0, config);
    };
    $[7] = buildPanelConfig;
    $[8] = isPanelOpen;
    $[9] = openPanel;
    $[10] = switchToPanel;
    $[11] = updatePanel;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleQuickAction = t3;
  let t4;
  if ($[13] !== badgeCount || $[14] !== handleQuickAction || $[15] !== isError || $[16] !== isInitialLoading || $[17] !== model || $[18] !== refetch || $[19] !== selectedTab || $[20] !== t) {
    t4 = {
      badgeCount,
      handleQuickAction,
      isError,
      isInitialLoading,
      model,
      refetch,
      selectedTab,
      setSelectedTab,
      t
    };
    $[13] = badgeCount;
    $[14] = handleQuickAction;
    $[15] = isError;
    $[16] = isInitialLoading;
    $[17] = model;
    $[18] = refetch;
    $[19] = selectedTab;
    $[20] = t;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
};
type AssignmentNotificationsPanelProps = ReturnType<typeof useAssignmentNotificationsController> & {
  className?: string;
  listClassName?: string;
  mode: "embedded" | "popover";
};
export const AssignmentNotificationsPanel = t0 => {
  const $ = _c(44);
  const {
    badgeCount,
    className,
    handleQuickAction,
    isError,
    isInitialLoading,
    listClassName: t1,
    mode,
    model,
    refetch,
    selectedTab,
    setSelectedTab,
    t
  } = t0;
  const listClassName = t1 === undefined ? "h-[min(60vh,34rem)]" : t1;
  let t2;
  if ($[0] !== handleQuickAction || $[1] !== isError || $[2] !== isInitialLoading || $[3] !== listClassName || $[4] !== model || $[5] !== refetch || $[6] !== t) {
    t2 = tab => {
      let content;
      if (isInitialLoading) {
        content = <AssignmentNotificationsLoading label={t("loading")} />;
      } else {
        if (isError) {
          content = <AssignmentNotificationsError title={t("error.title")} retryLabel={t("actions.retry")} onRetry={refetch} />;
        } else {
          const items = filterAssignmentNotificationItems(model.items, tab);
          content = items.length === 0 ? <AssignmentNotificationsEmpty title={t("empty.title")} description={t("empty.description")} /> : <div className="flex flex-col" data-testid="assignment-notification-list">{items.map(item => <AssignmentNotificationItemView key={item.id} item={item} onQuickAction={handleQuickAction} />)}</div>;
        }
      }
      return <ScrollArea className={listClassName} data-testid="assignment-notification-tab-viewport">{content}</ScrollArea>;
    };
    $[0] = handleQuickAction;
    $[1] = isError;
    $[2] = isInitialLoading;
    $[3] = listClassName;
    $[4] = model;
    $[5] = refetch;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const renderTabContent = t2;
  let t3;
  if ($[8] !== mode || $[9] !== t) {
    t3 = mode === "popover" ? <PopoverTitle className="text-base font-semibold">{t("title")}</PopoverTitle> : <h2 className="text-base font-semibold">{t("title")}</h2>;
    $[8] = mode;
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== badgeCount || $[12] !== t) {
    t4 = badgeCount > 0 && <Badge className="inline-flex h-5 shrink-0 items-center rounded-full bg-yellow-50 px-2 text-xs font-semibold whitespace-nowrap text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300">{t("requiresActionCount", {
        count: badgeCount
      })}</Badge>;
    $[11] = badgeCount;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <div className="flex min-w-0 items-center gap-2">{t3}{t4}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const headerTitle = t5;
  let t6;
  if ($[17] !== t) {
    t6 = t("title");
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== headerTitle || $[20] !== mode || $[21] !== t) {
    t7 = mode === "popover" ? <PopoverHeader className="border-b px-4 py-3">{headerTitle}<PopoverDescription>{t("description")}</PopoverDescription></PopoverHeader> : <div className="border-b px-4 py-3">{headerTitle}<p className="text-sm text-muted-foreground">{t("description")}</p></div>;
    $[19] = headerTitle;
    $[20] = mode;
    $[21] = t;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== setSelectedTab) {
    t8 = value => setSelectedTab(value as AssignmentNotificationPanelTab);
    $[23] = setSelectedTab;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== model || $[26] !== setSelectedTab || $[27] !== t) {
    t9 = tabs.map(tab_0 => <TabsTrigger key={tab_0} value={tab_0} onClick={() => setSelectedTab(tab_0)}>{t(`tabs.${tab_0}`)}<span className="text-xs font-normal text-muted-foreground tabular-nums">{getTabCount(tab_0, model.counters)}</span></TabsTrigger>);
    $[25] = model;
    $[26] = setSelectedTab;
    $[27] = t;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <div className="border-b px-3 py-2"><TabsList className="grid h-auto w-full grid-cols-3">{t9}</TabsList></div>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== renderTabContent || $[32] !== selectedTab) {
    t11 = tabs.map(tab_1 => <TabsContent key={tab_1} value={tab_1} className={cn("m-0", tab_1 !== selectedTab && "hidden")}>{tab_1 === selectedTab ? renderTabContent(tab_1) : null}</TabsContent>);
    $[31] = renderTabContent;
    $[32] = selectedTab;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== selectedTab || $[35] !== t10 || $[36] !== t11 || $[37] !== t8) {
    t12 = <Tabs value={selectedTab} onValueChange={t8} className="gap-0">{t10}{t11}</Tabs>;
    $[34] = selectedTab;
    $[35] = t10;
    $[36] = t11;
    $[37] = t8;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== className || $[40] !== t12 || $[41] !== t6 || $[42] !== t7) {
    t13 = <section role="region" aria-label={t6} className={className}>{t7}{t12}</section>;
    $[39] = className;
    $[40] = t12;
    $[41] = t6;
    $[42] = t7;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
};
function _temp(row) {
  return <div key={row} className="flex flex-col gap-2 border-b px-4 py-3 last:border-b-0"><div className="flex items-center justify-between gap-2"><Skeleton className="h-2.5 w-2/5" /><Skeleton className="h-4.5 w-24 rounded-full" /></div><Skeleton className="h-3.5 w-4/5" /><Skeleton className="h-2.5 w-1/2" /></div>;
}
function _temp2(category) {
  return <AssignmentNotificationStatusPill key={category} category={category} />;
}